// generateFAQPageJsonLd.js
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { formatISO } from 'date-fns';
import { DEFAULT_LANGUAGE } from '../resource/i18nCommon.js';
import { normalizeLang, getHreflang } from '../resource/langPath.js';

/**
 * Generate FAQPage JSON-LD structured data
 * @param {Object} options
 * @param {Array<{title: string, body: string, created_at: number, updated_at: number}>} options.faqItems - Array of FAQ items with title, body, created_at, updated_at
 * @param {string} options.pageUrl - Full URL of the FAQ page
 * @param {string} options.origin - Origin URL (default: 'https://swag.live')
 * @param {Array} options.supportedLanguages - Supported languages
 * @param {Object} options.i18n - i18n instance
 * @returns {Object} FAQPage JSON-LD object
 */
const generateFAQPageJsonLd = async ({
  faqItems = [],
  origin = 'https://swag.live',
  i18n,
} = {}) => {
  // Filter out invalid FAQ items
  const requiredFields = ['title', 'body', 'created_at', 'updated_at'];
  const validFaqItems = faqItems.filter(item =>
    requiredFields.every(field => item?.[field])
  );
  const { t } = i18n;
  const currentLanguage = i18n.language;
  const langPath =
    normalizeLang(currentLanguage) === DEFAULT_LANGUAGE
      ? ''
      : `/${normalizeLang(currentLanguage)}`;
  const pageUrlWithLang = `${origin}${langPath}/settings/faq`;
  const normalizedInLanguage = getHreflang(currentLanguage);

  const loadLanguagePromise = async () => {
    if (!i18n.hasResourceBundle(currentLanguage, TranslationNamespace.SEO)) {
      await i18n.reloadResources(currentLanguage, TranslationNamespace.SEO);
    }
  };

  await loadLanguagePromise();

  const faqTitle = t('seo_faq_title', {
    lng: currentLanguage,
    ns: TranslationNamespace.SEO,
  });
  const homeTitle = t('seo-home-title', {
    lng: currentLanguage,
    ns: TranslationNamespace.SEO,
  });
  const displayTitle = `${faqTitle} - ${homeTitle}`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        '@id': `${pageUrlWithLang}#webpage`,
        url: pageUrlWithLang,
        inLanguage: normalizedInLanguage,
        name: displayTitle,
        breadcrumb: {
          '@id': `${pageUrlWithLang}#breadcrumb`,
        },
        mainEntity: validFaqItems.map((item, index) => ({
          '@type': 'Question',
          '@id': `${pageUrlWithLang}#question-${index + 1}`,
          dateCreated: formatISO(item.created_at * 1000),
          dateModified: formatISO(item.updated_at * 1000),
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.body,
          },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrlWithLang}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: homeTitle,
            item: {
              '@id': `${origin}/#website`,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: displayTitle,
            item: {
              '@id': `${pageUrlWithLang}#webpage`,
            },
          },
        ],
      },
    ],
  };
};

export default generateFAQPageJsonLd;
